/**
 * RouteGuard — Unified route-level RBAC guard.
 *
 * Reads permissions directly from the routeRegistry, so Router and Sidebar
 * always agree. Falls back to ForbiddenPage (not a silent redirect).
 *
 * Usage:
 *   <RouteGuard rolesAllowed={['TREINADOR','GESTOR']} capability="arena.x.read">
 *     <CoachPage />
 *   </RouteGuard>
 *
 *   <RouteGuard superAdminOnly>
 *     <AdminPage />
 *   </RouteGuard>
 *
 *   <RouteGuard>  ← no props = any authenticated user
 *     <PublicPage />
 *   </RouteGuard>
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRBAC } from '../../contexts/RBACContext';
import { useProfile } from '../../contexts/ProfileContext';
import { shouldFunnelToWizard, WIZARD_PATH } from '../../utils/onboardingFunnel';
import ForbiddenPage from '../../pages/ForbiddenPage';

const RouteGuard = ({
  rolesAllowed = [],
  capability,
  superAdminOnly = false,
  fallback,           // optional — redirect path instead of ForbiddenPage
  children,
}) => {
  const { loading, activeRole, roles: userRoles, rbacData } = useRBAC();
  const { isSuperAdmin, hasCapability } = useProfile();

  // ── Loading spinner ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-t-green-700 border-gray-200 rounded-full mx-auto mb-2" />
          <p className="text-sm text-gray-500">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // ── Decision tree ──────────────────────────────────────────────
  // RULE: userRoles (token) authorise. activeRole is UX-only.

  // 0. DEFENSIVE: if userRoles is empty and route requires roles → deny
  //    Prevents access when RBAC data fails to load or session is broken.
  if ((!userRoles || userRoles.length === 0) && rolesAllowed && rolesAllowed.length > 0 && !isSuperAdmin) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[RouteGuard] DENIED (empty userRoles) — path=${window.location.pathname}`,
        `required=${JSON.stringify(rolesAllowed)}`,
      );
    }
    return fallback
      ? <Navigate to={fallback} replace />
      : <ForbiddenPage rolesAllowed={rolesAllowed} />;
  }

  // 1. Super admin always passes
  if (isSuperAdmin) return children;

  // 1.5. C2 (jul/2026) — Onboarding funnel: pending setup-role users are
  //      REDIRECTED (Navigate, not ForbiddenPage) to the wizard on any
  //      role-gated route. Closes the direct-URL leak: /start grants the
  //      GESTOR role immediately, so all 94 role-gated routes passed for
  //      a pending user — the only gate was CentralGate on /central.
  //      Predicate is the SAME shared helper CentralGate uses (single
  //      source — the two gates cannot disagree). Runs BEFORE capability
  //      and role steps: pending means "should not be on this route yet",
  //      regardless of how the route would authorise.
  //      Public-auth routes (empty rolesAllowed) stay FREE — the funnel
  //      protects half-configured club operation, it doesn't jail
  //      browsing. /manager/setup itself is behind StandaloneAuthRoute
  //      (ADR-3), outside RouteGuard — the funnel cannot self-lock.
  if (rolesAllowed && rolesAllowed.length > 0 && shouldFunnelToWizard(rbacData)) {
    return <Navigate to={WIZARD_PATH} replace />;
  }

  // 2. Super-admin-only route → deny
  if (superAdminOnly) {
    return fallback
      ? <Navigate to={fallback} replace />
      : <ForbiddenPage rolesAllowed={['SUPER_ADMIN']} />;
  }

  // 3. Capability grants access
  if (capability && hasCapability(capability)) return children;

  // 4. Public-auth route (empty rolesAllowed) → allow
  if (!rolesAllowed || rolesAllowed.length === 0) return children;

  // 5. Token-based role check — activeRole NEVER grants access
  const hasTokenRole = userRoles.some(r => rolesAllowed.includes(r));

  if (hasTokenRole) return children;

  // ── Denied ─────────────────────────────────────────────────────
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `[RouteGuard] DENIED — path=${window.location.pathname}`,
      `activeRole=${activeRole}`,
      `userRoles=${JSON.stringify(userRoles)}`,
      `required=${JSON.stringify(rolesAllowed)}`,
    );
  }

  return fallback
    ? <Navigate to={fallback} replace />
    : <ForbiddenPage rolesAllowed={rolesAllowed} />;
};

export default RouteGuard;
